import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowUpRight } from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { Reveal, Overline } from "../components/Reveal";
import { WhyChooseUs } from "../components/home/WhyChooseUs";
import { CTASection } from "../components/home/CTASection";
import { IMG, VALUES, COMMITMENTS } from "../data/content";

const highlights = [
  "Established in 2019",
  "100% Black-owned",
  "Level 1 B-BBEE",
  "Experienced professionals",
  "Modern equipment",
  "Environmentally responsible practices",
  "Customer-focused service",
];

export default function About() {
  return (
    <main data-testid="about-page">
      <PageHeader
        eyebrow="About Us"
        title="Built on Reliability. Driven by Excellence."
        subtitle="A trusted South African property support partner helping businesses and property owners maintain clean, healthy and well-maintained environments."
        image={IMG.glassBuilding}
      />

      {/* History */}
      <section className="bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid gap-14 lg:grid-cols-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="grid grid-cols-2 gap-4">
              <img src={IMG.gardener} alt="Grounds maintenance" className="rounded-sm h-72 w-full object-cover" />
              <img src={IMG.cleaningVacuum} alt="Professional cleaning" className="rounded-sm h-72 w-full object-cover mt-10" />
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <Overline>Our Story</Overline>
            <Reveal>
              <h2 className="mt-6 font-display text-3xl md:text-4xl tracking-tight text-sml-charcoal text-balance">
                A dependable partner since 2019
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-8 space-y-5 text-sml-charcoal/70 leading-relaxed">
                <p>
                  Founded in 2019, SML Property Solutions has grown into a trusted property support
                  service provider dedicated to helping businesses and property owners maintain clean,
                  healthy and well-maintained environments.
                </p>
                <p>
                  We understand the importance of first impressions — clean workplaces, healthy landscapes
                  and hygienic environments contribute to productivity, customer satisfaction and property value.
                </p>
              </div>
            </Reveal>
            <div className="mt-8 flex flex-wrap gap-2.5">
              {highlights.map((h) => (
                <span key={h} className="text-sm border border-sml-border rounded-sm px-3.5 py-2 text-sml-charcoal/80">
                  {h}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Vision / Mission */}
      <section data-testid="vision-mission" className="bg-sml-forest text-white py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid gap-px bg-white/10 border border-white/10 rounded-sm overflow-hidden md:grid-cols-2">
          <div className="bg-sml-forest p-10 lg:p-14">
            <span className="font-mono-sml text-xs uppercase tracking-[0.2em] text-sml-bronze">01 — Our Vision</span>
            <p className="mt-6 font-display text-2xl md:text-3xl leading-snug">
              To become one of South Africa's leading property support service providers by delivering
              innovative, sustainable and customer-focused solutions that improve the quality of
              environments and communities.
            </p>
          </div>
          <div className="bg-sml-green p-10 lg:p-14">
            <span className="font-mono-sml text-xs uppercase tracking-[0.2em] text-sml-bronze">02 — Our Mission</span>
            <p className="mt-6 font-display text-2xl md:text-3xl leading-snug">
              To provide professional horticultural, cleaning, hygiene and facility support services that
              enhance cleanliness, hygiene, safety and environmental sustainability while creating value
              for our clients and stakeholders.
            </p>
          </div>
        </div>
      </section>

      {/* Values — numbered manifesto */}
      <section data-testid="values-section" className="bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="max-w-2xl mb-16">
            <Overline>Our Values</Overline>
            <Reveal>
              <h2 className="mt-6 font-display text-3xl md:text-4xl lg:text-5xl tracking-tight text-sml-charcoal">
                The principles behind every service
              </h2>
            </Reveal>
          </div>
          <div className="divide-y divide-sml-border border-y border-sml-border">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.04}>
                <div className="group grid md:grid-cols-12 gap-4 py-8 items-baseline hover:bg-sml-mist -mx-5 px-5 transition-colors">
                  <span className="md:col-span-1 font-mono-sml text-sm text-sml-bronze">0{i + 1}</span>
                  <h3 className="md:col-span-4 font-display text-2xl md:text-3xl text-sml-charcoal">{v.title}</h3>
                  <p className="md:col-span-7 text-sml-charcoal/65 leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Commitments */}
      <section data-testid="commitment-section" className="bg-sml-mist py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="max-w-2xl mb-14">
            <Overline>Our Promise</Overline>
            <Reveal>
              <h2 className="mt-6 font-display text-3xl md:text-4xl lg:text-5xl tracking-tight text-sml-charcoal">
                Our Commitment to Excellence
              </h2>
            </Reveal>
          </div>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {COMMITMENTS.map((c, i) => {
              const Icon = Icons[c.icon] || Icons.CheckCircle;
              return (
                <Reveal key={c.title} delay={(i % 3) * 0.08}>
                  <div className="h-full bg-white border border-sml-border rounded-sm p-8 hover:border-sml-forest transition-colors" data-testid={`commitment-${i}`}>
                    <Icon className="h-7 w-7 text-sml-green" />
                    <h3 className="mt-6 font-display text-xl text-sml-charcoal">{c.title}</h3>
                    <p className="mt-3 text-sm text-sml-charcoal/60 leading-relaxed">{c.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <div className="mt-14">
            <Link
              to="/contact"
              data-testid="about-cta-button"
              className="group inline-flex items-center gap-2 rounded-sm bg-sml-forest text-white font-medium px-7 py-4 hover:bg-sml-green transition-colors active:scale-[0.98]"
            >
              Request a Service Proposal
              <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>
      </section>

      <WhyChooseUs />
      <CTASection />
    </main>
  );
}
